// utils/Http.js
import axios from 'axios';

// ============================================
// GLOBAL API CLIENT (mutable)
// ============================================
let apiClient = null;
let currentBaseURL = null;

// ============================================
// REDIRECT TO LOGIN
// ============================================
const redirectToLogin = () => {
  // Don't loop if we're already on /login
  if (window.location.pathname.startsWith('/login')) return;

  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/login';
};

// ============================================
// GET BASE URL
// ============================================
const getBaseURL = async () => {
  if (window.electronAPI) {
    try {
      const result = await window.electronAPI.loadConfig();
      if (result.success && result.data.apiUrl) {
        return result.data.apiUrl;
      }
    } catch (err) {
      // ignore, fall through
    }
  }

  const envUrl =
    process.env.REACT_APP_API_PROD_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    'http://127.0.0.1:8000';
  return envUrl;
};

// ============================================
// REFRESH STATE (shared across all requests)
// ============================================
let isRefreshing = false;
let pendingQueue = []; // { resolve, reject, config }

const flushQueue = (error, newToken = null) => {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newToken}`;
      resolve(apiClient(config));
    }
  });
  pendingQueue = [];
};

// ============================================
// INITIALIZE API CLIENT
// ============================================
export const initializeAPI = async () => {
  const baseURL = await getBaseURL();
  currentBaseURL = baseURL;

  apiClient = axios.create({
    baseURL,
    timeout: 50000,
  });

  // ---------- REQUEST INTERCEPTOR ----------
  apiClient.interceptors.request.use(
    (config) => {
      if (config.data instanceof FormData) {
        if (typeof config.headers?.delete === 'function') {
          config.headers.delete('Content-Type');
        } else if (config.headers) {
          delete config.headers['Content-Type'];
        }
      }

      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // ---------- RESPONSE INTERCEPTOR ----------
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};
      const status = error.response?.status;

      // Refresh endpoint itself failed → hard logout, no retry
      const isRefreshCall = (originalRequest.url || '').includes(
        '/api/token/refresh/'
      );
      if (isRefreshCall) {
        redirectToLogin();
        return Promise.reject(error);
      }

      // Only handle 401 as "token expired". 403 = permission denied, DO NOT logout.
      if (status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      isRefreshing = true;

      try {
        const response = await axios.post(
          `${currentBaseURL}/api/token/refresh/`,
          { refresh: refreshToken }
        );

        const newAccessToken =
          response.data.access ?? response.data.access_token;

        if (!newAccessToken) {
          throw new Error('Refresh response missing access token');
        }

        localStorage.setItem('access_token', newAccessToken);

        // Support rotating refresh tokens if the backend sends one
        const newRefreshToken =
          response.data.refresh ?? response.data.refresh_token;
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        // Flush everything waiting on this refresh
        flushQueue(null, newAccessToken);

        // Retry the original request
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        flushQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );

  return apiClient;
};

// ============================================
// GET API CLIENT
// ============================================
const getClient = () => {
  if (!apiClient) {
    throw new Error('API not initialized. Call initializeAPI() first.');
  }
  return apiClient;
};

const api = {
  get: (...args) => getClient().get(...args),
  post: (...args) => getClient().post(...args),
  put: (...args) => getClient().put(...args),
  patch: (...args) => getClient().patch(...args),
  delete: (...args) => getClient().delete(...args),

  get raw() {
    return getClient();
  },

  getBaseURL: () => currentBaseURL,
};

export default api;